import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';

/**
 * Dropdown menu — a small accessible primitive used by the topbar profile
 * menu and any future "kebab" action menus on table rows.
 *
 * Behaviour
 * ─────────
 * - Click the trigger to toggle; click outside or press Escape to close.
 * - The menu is a `role="menu"` with `role="menuitem"` children; items
 *   handle their own onClick. The menu closes automatically after an
 *   item activates so the caller doesn't need to manage state.
 * - Keyboard focus is moved into the menu on open; first item gets focus.
 *
 * Out of scope (for now)
 * ──────────────────────
 * Full arrow-key roving — we ship a basic version and can graduate to
 * `@headlessui/react`'s Menu once the design needs richer interactions.
 */

interface DropdownContextValue {
  open: boolean;
  toggle: () => void;
  close: () => void;
  menuId: string;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
}

const DropdownContext = createContext<DropdownContextValue | null>(null);

function useDropdown(): DropdownContextValue {
  const ctx = useContext(DropdownContext);
  if (!ctx) throw new Error('Dropdown subcomponents must be rendered inside <Dropdown>.');
  return ctx;
}

export function Dropdown({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((v) => !v), []);

  const value = useMemo<DropdownContextValue>(
    () => ({ open, toggle, close, menuId, triggerRef }),
    [open, toggle, close, menuId],
  );

  return (
    <DropdownContext.Provider value={value}>
      <div className="relative">{children}</div>
    </DropdownContext.Provider>
  );
}

interface DropdownTriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
}

export function DropdownTrigger({ children, ...props }: DropdownTriggerProps) {
  const { open, toggle, menuId, triggerRef } = useDropdown();
  return (
    <button
      type="button"
      ref={triggerRef}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-controls={menuId}
      onClick={(e) => {
        toggle();
        props.onClick?.(e);
      }}
      {...props}
    >
      {children}
    </button>
  );
}

interface DropdownMenuProps {
  children: ReactNode;
  /** Tailwind classes overriding the default positioning (defaults to right-aligned). */
  className?: string;
}

export function DropdownMenu({ children, className }: DropdownMenuProps) {
  const { open, close, menuId, triggerRef } = useDropdown();
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click and Escape.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (menuRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close, triggerRef]);

  // Focus the first focusable element when the menu opens.
  useEffect(() => {
    if (!open) return;
    const first = menuRef.current?.querySelector<HTMLElement>(
      'button:not([disabled]), [href], [role="menuitem"]:not([aria-disabled="true"])',
    );
    first?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div
      id={menuId}
      ref={menuRef}
      role="menu"
      aria-orientation="vertical"
      className={
        className ??
        'absolute right-0 z-30 mt-2 min-w-[16rem] overflow-hidden rounded-md border border-surface-border bg-surface shadow-lg ring-1 ring-black/5'
      }
    >
      {children}
    </div>
  );
}

interface DropdownItemProps {
  children: ReactNode;
  onSelect?: () => void;
  disabled?: boolean;
  /** Visual variant — `danger` paints the item red for destructive actions. */
  variant?: 'default' | 'danger';
}

export function DropdownItem({
  children,
  onSelect,
  disabled,
  variant = 'default',
}: DropdownItemProps) {
  const { close } = useDropdown();
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={() => {
        if (disabled) return;
        onSelect?.();
        close();
      }}
      className={
        variant === 'danger'
          ? 'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-error-600 hover:bg-error-50 disabled:cursor-not-allowed disabled:opacity-60 dark:text-error-400 dark:hover:bg-error-500/15'
          : 'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-60'
      }
    >
      {children}
    </button>
  );
}

export function DropdownSeparator() {
  return <div role="separator" className="my-1 h-px bg-surface-border" />;
}

export function DropdownHeader({ children }: { children: ReactNode }) {
  return <div className="px-3 py-2 text-xs text-slate-500">{children}</div>;
}
